import { ArrowRight, BookOpen, Users, TrendingUp, Anchor, CheckCircle, ExternalLink, Target } from "lucide-react";
import { Link } from "react-router-dom";

export default function Home({ userRole }) {
  const values = [
    {
      icon: Users,
      title: "Accessibility",
      description: "We believe financial literacy should not be a privilege. Our services are practical, easy to understand, and available to communities often left behind by traditional systems."
    },
    {
      icon: TrendingUp,
      title: "Empowerment",
      description: "We equip you with knowledge and tools to make confident decisions. Our goal is to help you stand on your own financial foundation, not depend on us forever."
    },
    {
      icon: CheckCircle,
      title: "Trust",
      description: "Your financial journey deserves honesty and transparency. We build lasting relationships grounded in integrity and reliability."
    },
    {
      icon: Anchor,
      title: "Community",
      description: "We thrive on connection. By fostering networks of clients, mentors, and partners, Anchor Lane creates a supportive ecosystem where everyone grows together."
    }
  ];

  const services = [
    {
      title: "Financial Literacy Education",
      description: "Comprehensive programs covering budgeting, saving, and fundamental money management principles that build confidence and competence.",
      icon: BookOpen,
      image: "https://images.unsplash.com/photo-1434626881859-194d67b2b86f?w=600&h=400&fit=crop"
    },
    {
      title: "Small Business Consulting",
      description: "Strategic planning and practical financial guidance designed specifically for entrepreneurs and small business owners seeking sustainable growth.",
      icon: TrendingUp,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop"
    },
    {
      title: "Personalized Financial Guidance",
      description: "One-on-one consultations tailored to your unique situation, goals, and challenges — because no two financial journeys are the same.",
      icon: Users,
      image: "https://images.unsplash.com/flagged/photo-1551135049-83f3419ef05c?w=600&h=400&fit=crop"
    }
  ];

  return (
    <div className="min-h-full">
      {/* Hero Section */}
      <div className="relative bg-[#1B3A5F] text-white overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1551135049-8a33b5883817?w=1600&h=800&fit=crop"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_a61284df-68cb-4f82-b4d6-5cabd88681a3/artifacts/frjmmhxb_IMG_2281.jpeg"
                alt="Anchor Lane Logo"
                className="h-24 w-auto"
                data-testid="hero-logo"
              />
            </div>
            <h1 className="heading-font text-5xl lg:text-7xl font-bold mb-6 tracking-tight">
              ANCHOR LANE
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-4 max-w-3xl mx-auto">
              Empowering Financial Independence Through Accessible Education
            </p>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Financial knowledge should be accessible to everyone. We offer practical guidance, 
              secure solutions, and education that truly empowers people to build lasting stability.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/goals-impact"
                className="inline-flex items-center justify-center bg-[#C5A059] hover:bg-[#9F8045] text-white px-8 py-4 rounded-md font-medium shadow-lg transition-colors"
                data-testid="cta-goals-btn"
              >
                <Target className="mr-2 h-5 w-5" />
                Explore Our Impact
              </Link>
              <Link
                to="/education"
                className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-md font-medium transition-colors"
                data-testid="cta-learn-btn"
              >
                Browse Resources
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 lg:px-8 space-y-16">
        {/* Our Story */}
        <section data-testid="our-story-section">
          <div className="text-center mb-12">
            <h2 className="heading-font text-4xl lg:text-5xl font-bold text-[#1B3A5F] mb-4">
              Our Story
            </h2>
            <div className="w-20 h-1 bg-[#C5A059] mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1551135049-8a33b5883817?w=800&h=600&fit=crop"
                alt="Team collaboration"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
            <div className="space-y-6">
              <h3 className="heading-font text-2xl font-semibold text-[#1B3A5F]">
                The Beginning of Anchor Lane
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Anchor Lane was founded with a simple belief: financial knowledge should be 
                accessible to everyone. Too often, individuals and small business owners feel 
                left out of traditional financial systems. We set out to change that — offering 
                practical guidance, secure solutions, and education that truly empowers people 
                to build lasting stability and independence.
              </p>
              <a
                href="https://anchorllane.wixsite.com/anchor-lane/about"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-[#1B3A5F] hover:text-[#2C5282] font-medium"
                data-testid="learn-more-link"
              >
                Learn More About Us
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="bg-slate-50 -mx-6 px-6 py-12 lg:-mx-8 lg:px-8" data-testid="mission-vision-section">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#1B3A5F]/10 rounded-lg mr-4">
                    <Anchor className="h-8 w-8 text-[#1B3A5F]" />
                  </div>
                  <h3 className="heading-font text-2xl font-bold text-[#1B3A5F]">Our Mission</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Anchor Lane's mission is to empower individuals, families, and small businesses 
                  with financial literacy, practical guidance, and secure solutions. We focus on 
                  simplifying money management and business planning so clients gain the confidence 
                  to make smart, lasting financial decisions.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-[#C5A059]/10 rounded-lg mr-4">
                    <TrendingUp className="h-8 w-8 text-[#C5A059]" />
                  </div>
                  <h3 className="heading-font text-2xl font-bold text-[#1B3A5F]">Our Vision</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Our vision is to become the trusted guide that transforms financial uncertainty 
                  into confidence and opportunity. We aim to build a future where financial 
                  independence is accessible to everyone, creating stronger communities and 
                  sustainable growth.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section data-testid="services-section">
          <div className="text-center mb-12">
            <h2 className="heading-font text-4xl lg:text-5xl font-bold text-[#1B3A5F] mb-4">
              How We Serve You
            </h2>
            <div className="w-20 h-1 bg-[#C5A059] mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Practical, accessible financial solutions designed to empower your journey toward independence
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden card-hover"
                  data-testid={`service-card-${index}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex justify-center mb-4">
                      <div className="p-3 bg-[#1B3A5F]/10 rounded-full">
                        <Icon className="h-8 w-8 text-[#1B3A5F]" />
                      </div>
                    </div>
                    <h3 className="heading-font text-xl font-semibold text-[#1B3A5F] mb-3 text-center">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-center leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Values */}
        <section data-testid="values-section">
          <div className="text-center mb-12">
            <h2 className="heading-font text-4xl lg:text-5xl font-bold text-[#1B3A5F] mb-4">
              What We Stand For
            </h2>
            <div className="w-20 h-1 bg-[#C5A059] mx-auto mb-6"></div>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Our values guide every program, consultation, and solution we deliver
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 card-hover"
                  data-testid={`value-card-${index}`}
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="p-3 bg-[#C5A059]/10 rounded-lg">
                        <Icon className="h-8 w-8 text-[#C5A059]" />
                      </div>
                    </div>
                    <div>
                      <h3 className="heading-font text-xl font-semibold text-[#1B3A5F] mb-3">
                        {value.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#1B3A5F] text-white rounded-lg shadow-md p-8 lg:p-12 -mx-6 lg:-mx-8" data-testid="bottom-cta">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-font text-3xl lg:text-4xl font-bold mb-4">
              Ready to Transform Your Financial Future?
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              Join our community and start your journey toward lasting financial independence and security
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/goals-impact"
                className="inline-flex items-center justify-center bg-[#C5A059] hover:bg-[#9F8045] text-white px-8 py-3 rounded-md font-medium shadow-sm transition-colors"
                data-testid="bottom-cta-goals"
              >
                See Our Goals & Impact
              </Link>
              <Link
                to="/education"
                className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-3 rounded-md font-medium transition-colors"
                data-testid="bottom-cta-education"
              >
                Explore Educational Resources
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-[#1B3A5F] text-white py-6 mt-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-white/80 text-sm">© 2026 Anchor Lane</p>
        </div>
      </footer>
    </div>
  );
}
